import { useEffect } from 'react';

/**
 * Custom hook to handle browser unload events
 * Only sends logout signal to server on actual browser/tab close
 * SessionStorage automatically clears on browser close, so no manual clearing needed
 * @returns {void}
 */
export const useBeforeUnload = () => {
  useEffect(() => {
    let isLoggingOut = false;
    let isPageRefresh = false;

    /**
     * Checks if the current URL has the isRedirectingToClinical query parameter
     * @returns {boolean} True if redirecting to clinical service
     */
    const isRedirectingToClinical = () => {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('isRedirectingToClinical') === 'true';
    };

    /**
     * Detects if this is a page refresh vs browser close
     * @returns {boolean} True if this is likely a page refresh
     */
    const detectPageRefresh = () => {
      // Check if the page is being refreshed by looking at performance navigation
      if (performance && performance.navigation) {
        return performance.navigation.type === 1; // TYPE_RELOAD = 1
      }
      
      // Fallback: check if we have a recent page load timestamp
      const lastPageLoad = sessionStorage.getItem('lastPageLoad');
      const now = Date.now();
      sessionStorage.setItem('lastPageLoad', now.toString());
      
      // If we have a very recent page load (within 1 second), it's likely a refresh
      if (lastPageLoad && (now - parseInt(lastPageLoad)) < 1000) {
        return true;
      }
      
      return false;
    };

    /**
     * Sends logout signal to server when browser/tab is actually closed
     */
    const sendLogoutSignal = () => {
      // Don't send signal if redirecting to clinical service
      if (isRedirectingToClinical()) {
        return;
      }

      // Don't send signal if this is a page refresh
      if (isPageRefresh) {
        return;
      }

      if (!isLoggingOut && !window.location.href.includes('login')) {
        isLoggingOut = true;
        
        try {
          const sessionId = sessionStorage.getItem('systemSessionId');
          const accessToken = sessionStorage.getItem('accessToken');
          if (!sessionId || !accessToken) {
            return;
          }

          const apiBaseUrl = import.meta.env.VITE_APP_API_URL || window.location.origin;
          fetch(`${apiBaseUrl}/auth/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              sessionId: sessionId,
              logoutType: 'ErrorLogout',
            }),
            keepalive: true,
          }).catch(() => {
            // Best-effort call only.
          });
        } catch (error) {
          // Silently handle error
        }
      }
    };

    /**
     * Handles the beforeunload event - detects page refresh vs browser close
     */
    const handleBeforeUnload = () => {
      // Don't process if redirecting to clinical service
      if (isRedirectingToClinical()) {
        return;
      }

      // Detect if this is a page refresh
      isPageRefresh = detectPageRefresh();
      
      if (isPageRefresh) {
        return;
      }

      // For actual browser close, sessionStorage will be automatically cleared
      // We only need to send a logout signal to the server if needed
    };

    /**
     * Handles the pagehide event - sends logout signal when page is being unloaded
     */
    const handlePageHide = (event) => {
      // Don't send signal if redirecting to clinical service
      if (isRedirectingToClinical()) {
        return;
      }

      // Check if this is a page refresh by looking at the persisted property
      // If persisted is true, the page is being cached and will be restored
      // If persisted is false, the page is being unloaded permanently
      if (event.persisted) {
        return;
      }

      // Send logout signal for actual browser close
      sendLogoutSignal();
    };

    /**
     * Handles the unload event - fallback for older browsers
     */
    const handleUnload = () => {
      // Don't send signal if redirecting to clinical service
      if (isRedirectingToClinical()) {
        return;
      }

      // Don't send signal if this is a page refresh
      if (isPageRefresh) {
        return;
      }

      // Send logout signal for actual browser close
      sendLogoutSignal();
    };

    /**
     * Handles the visibilitychange event - detects when user switches tabs or minimizes
     */
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // User has switched tabs or minimized - set a timestamp
        sessionStorage.setItem('lastHiddenTime', Date.now().toString());
      } else {
        // User has returned to the tab
        const lastHiddenTime = parseInt(sessionStorage.getItem('lastHiddenTime') || '0');
        const now = Date.now();
        const timeAway = now - lastHiddenTime;
        
        // If user was away for more than 15 minutes, consider logging them out
        if (lastHiddenTime > 0 && timeAway > 15 * 60 * 1000) {
          // Don't logout if redirecting to clinical service
          if (!window.location.href.includes('login') && !isLoggingOut && !isRedirectingToClinical()) {
            isLoggingOut = true;
            window.location.href = '/login';
          }
        }
        
        sessionStorage.removeItem('lastHiddenTime');
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('unload', handleUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('unload', handleUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
};
